import { useEffect, useRef } from 'react'

import { putPatientBackground } from '../apis/patientBackground'
import { useAnyCompassEmbed } from '../contexts/EmbedContext'
import {
  buildPatientBackgroundPayload,
  hasPatientBackground,
} from '../helpers/patientBackground'
import { isPatientNameMismatch } from '../helpers/patientMismatch'

interface SendJob {
  id: string
  payloadJson: string
  payload: ReturnType<typeof buildPatientBackgroundPayload>
}

/**
 * 親から INIT で受けた患者背景(頭書き + 身長体重)を、対象薬歴が確定した時点で back へ預ける。
 *
 * 送るタイミングが「薬歴を開いたとき」なのは、INIT が iframe マウント時に届くのに対し
 * 薬歴 ID はその後に確定するため。SOAP 生成の起点(録音停止)より十分手前になる。
 *
 * 送るのは「最後に送った {薬歴, 内容} と違うとき」だけ。back は yakureki 単位の冪等
 * upsert なので同じ内容を重ねて送っても害は無く、抑止すべきは polling / 再 render での
 * 連打だけ。薬歴 ID 単位の一回きり (旧 sentIdRef) にはしない: 親が INIT を再送する世代
 * では頭書きが編集されて届き直すことがあり、ID でキーすると編集後の内容が二度と back へ
 * 届かなくなる (log/193 work-log/7 §3-d)。
 *
 * 預けない条件が 2 つある:
 * - INIT の患者が deeplink の患者 (SSoT) と不一致 = 親が別患者へ移った後の INIT。
 *   開いたままの薬歴に**別患者の頭書き**を書き込むことになるため送らない
 *   (突合ガードと同じ判定。表示だけでなく書き込みも止める)
 * - 中身が空で、かつこの薬歴に送信実績が無い。back は「レコード無し = 未受領」と
 *   「中身が空 = セクション省略」を区別するので、最初から空なら送らない。
 *   送信済みなら空も送る (= 親側で最後の記載を消した編集の伝搬。送らないと
 *   撤回済みのアレルギー等が back に残り SOAP 生成に注入され続ける)
 *
 * 送信は直列化する。並走させると古い内容の PUT が後からコミットされたとき、back は
 * 旧内容のまま・こちらの記録は新内容送信済みになり、再送も二度と起きなくなるため。
 * 失敗しても黙って諦める(頭書きが無ければ back は `# 患者背景` を省略するだけで、
 * SOAP 生成そのものは従来どおり成立する)。
 */
export const useSyncPatientBackground = (yakurekiId: string | null): void => {
  const { isEmbedded, patientContext, deeplinkPatientName } =
    useAnyCompassEmbed()
  // 最後に送信が**成功**した {薬歴 ID, payload 内容}。同一内容の再送信の抑止と、
  // 「この薬歴に送信実績があるか」(空 payload を送るかの判定) の両方に使う。
  const lastSentRef = useRef<{ id: string; payloadJson: string } | null>(null)
  // 送信中の job。null でなければ drain が動いている。
  const inFlightJobRef = useRef<SendJob | null>(null)
  // 送信中に届いた最新の差分。完了後に 1 回だけ送る (編集の連続を 1 本化)。
  const pendingRef = useRef<SendJob | null>(null)

  useEffect(() => {
    if (!isEmbedded || !yakurekiId) return
    if (isPatientNameMismatch(deeplinkPatientName, patientContext?.knjName)) {
      return
    }

    // INIT の param をそのまま渡す (頭書き・身長体重・氏名 echo の 3 つを含む)。
    // knjName の echo は頭書きと同じ INIT から取る (= この頭書きが誰のものとして
    // 届いたかの出所表示)。SSoT (deeplink) ではない: 突合はこの直前の mismatch ガードが
    // SSoT で行っており、echo は back 側で event.patient_name と突き合わせる別層。
    const payload = buildPatientBackgroundPayload(patientContext)
    // 「送信実績」は成功済みだけでなく送信中・待機中も含める。初回 PUT の応答待ち中に
    // クリアの INIT が届く順序でも、クリアを落とさないため。
    const hasSentForThisYakureki =
      lastSentRef.current?.id === yakurekiId ||
      inFlightJobRef.current?.id === yakurekiId ||
      pendingRef.current?.id === yakurekiId
    if (!hasPatientBackground(payload) && !hasSentForThisYakureki) {
      return
    }

    // buildPatientBackgroundPayload はキー順が固定なので JSON 文字列比較で足りる。
    const payloadJson = JSON.stringify(payload)
    const job: SendJob = { id: yakurekiId, payloadJson, payload }

    const isSameAsLastSent = (target: SendJob): boolean =>
      lastSentRef.current !== null &&
      lastSentRef.current.id === target.id &&
      lastSentRef.current.payloadJson === target.payloadJson

    const drain = async (first: SendJob): Promise<void> => {
      try {
        let current: SendJob | null = first
        while (current !== null) {
          inFlightJobRef.current = current
          try {
            await putPatientBackground(current.id, current.payload)
            lastSentRef.current = {
              id: current.id,
              payloadJson: current.payloadJson,
            }
          } catch (e) {
            // 記録しない = 次の INIT / 薬歴遷移で自然に再試行される。
            console.warn(
              '[anycompass-embed] failed to sync patient background',
              e
            )
          }
          const next = pendingRef.current
          pendingRef.current = null
          current = next !== null && !isSameAsLastSent(next) ? next : null
        }
      } finally {
        inFlightJobRef.current = null
      }
    }

    if (inFlightJobRef.current !== null) {
      pendingRef.current = job
      return
    }
    if (isSameAsLastSent(job)) return
    void drain(job)
  }, [isEmbedded, patientContext, yakurekiId, deeplinkPatientName])
}
